import { ReactElement, useMemo } from "react";
import {
	NotificationViewModel,
	NotificationKind,
	AvailableNotificationViewModel
} from "client/Api/Notifications/notifications";
import {
	ActivityCard,
	ActivityCardIconData,
	ActivityCardIconKind,
	PersonAvatarData
} from "./Card/ActivityCard";
import { ActivityContent } from "./Content/ActivityContent";
import {
	ActionListItem,
	Anchor,
	FontIconName,
	SvgIconValue
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { useActivityFeedStore } from "../store";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import spacesStyles from "../ActivityFeedSpaces.module.less";
import { getDocumentKind } from "client_legacy/Documents/fileExtensions";
import { ActivityActions } from "./Actions/ActivityActions";
import { getUserProfileImageUrl } from "client/Api/UserProfile/service";
import classNames from "classnames";
import {
	InvocableAvailableNotificationViewModel,
	isInvocableAvailableNotificationViewModel
} from "./Invocation/ActivityInvocation";
import { generateTrackingId, isNotNil } from "client/Misc/utils";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";
import { TimeZone } from "client/DateAndTime/timezones";
import { useUserTimezone } from "client/Hooks";
import { AppType } from "client/Api/Apps/common";
import { useAnalyticsStore } from "client/Features/Analytics/analyticsStore";
import { AnalyticsAppEventData } from "client/Api/Analytics/analytics";
import { never } from "client_legacy/ndaw/errors";

export type ActivityProps = {
	categoryName: string;
	activity: AvailableNotificationViewModel;
	onDelete(): void;
	onMute(): void;
	onActivityChanged(): void;
	onActivityInvoked?: () => void;
};

const {
	Description,
	Footer
} = ActivityCard;

const {
	ActionList,
	TimeIndication
} = Footer;

export const Activity = (props: ActivityProps): ReactElement => {
	const {
		categoryName,
		activity,
		onDelete,
		onMute,
		onActivityChanged,
		onActivityInvoked
	} = props;

	const userTimezone: TimeZone = useUserTimezone();

	const {
		id,
		canBeDeletedByUser,
		availableFrom,
		color
	} = activity;

	const dismiss: ((id: number) => void) = useActivityFeedStore(state => state.dismiss);
	const pendingDismissedIds: ReadonlyArray<number> = useActivityFeedStore(state => state.pendingDismissedIds);
	const isVisuallyDismissed: boolean = activity.isDismissed || pendingDismissedIds.some(dismissedId => dismissedId === id);
	const addAnalyticsAppEvent: (data: AnalyticsAppEventData) => void = useAnalyticsStore(x => x.addAppEvent);

	const deleteHandler = (): void => {
		if (canBeDeletedByUser) {
			onDelete();
		}
	};

	const muteHandler = (): void => {
		onMute();
	};

	const invoke: (activity: InvocableAvailableNotificationViewModel) => void = useActivityFeedStore(state => state.setInvokedActivity);

	const invokeHandler = useMemo(
		(): (() => void) | undefined => {
			if (isInvocableAvailableNotificationViewModel(activity)) {
				return () => {
					const appType: AppType | null = activityKindToAppType(activity);

					if (isNotNil(appType)) {
						addAnalyticsAppEvent({
							appType
						});
					}

					invoke(activity);
					onActivityInvoked?.();
				};
			}

			return undefined;
		},
		[
			activity,
			invoke,
			addAnalyticsAppEvent,
			onActivityInvoked
		]
	);

	const trackingId: string = generateTrackingId("activity", `|${id}`);

	return (
		<Anchor
			className={
				classNames(
					spacesStyles.activityItem,
					{
						[spacesStyles.activityItem_defaultCursor]: invokeHandler === undefined
					}
				)
			}
			onClick={invokeHandler}
			trackingId={trackingId}
		>
			<ActivityCard
				hasFooter
				categoryName={categoryName}
				isDismissed={isVisuallyDismissed}
				icon={getIconTemplateData(activity, false)}
				color={color}
				onDismiss={() => dismiss(id)}
			>
				<Description>
					<ActivityContent
						activity={activity}
						onActivityChanged={onActivityChanged}
					/>
				</Description>
				<Footer>
					{availableFrom &&
						<TimeIndication timestamp={parseDtoDate(availableFrom)} timezone={userTimezone} />
					}
					<ActionList
						trackingId={generateTrackingId(trackingId, "__actions")}
						menuTitle={categoryName}
					>
						<ActivityActions
							activity={activity}
						/>

						<ActionListItem
							icon="bell-slash"
							text={localize("_Mute")}
							onClick={muteHandler}
							trackingId="mute"
						/>

						{canBeDeletedByUser && (
							<ActionListItem
								icon="trash-can"
								text={localize("_Delete")}
								onClick={deleteHandler}
								trackingId="delete"
							/>
						)}
					</ActionList>
				</Footer>
			</ActivityCard>
		</Anchor>
	);
};

Activity.displayName = "Activity";

export function getIconTemplateData(activity: AvailableNotificationViewModel, dontRenderAppIcon: boolean): ActivityCardIconData {
	if (activity.appIcon) {
		if (dontRenderAppIcon) {
			return {
				isTransparent: false,
				kind: ActivityCardIconKind.None
			};
		}
		return {
			isTransparent: false,
			kind: ActivityCardIconKind.AppIcon,
			appIcon: activity.appIcon
		};
	}

	const icon: FontIconName | SvgIconValue | null = getIcon(activity);
	if (icon) {
		return {
			isTransparent: typeof icon !== "string" && icon.kind === "svg",
			kind: ActivityCardIconKind.Icon,
			icon: icon
		};
	}

	const personAvatar: PersonAvatarData | null = getPersonAvatar(activity);
	if (personAvatar) {
		return {
			isTransparent: false,
			kind: ActivityCardIconKind.PersonAvatar,
			personAvatar
		};
	}

	return {
		isTransparent: false,
		kind: ActivityCardIconKind.None
	};
}

function getIcon(activity: NotificationViewModel): FontIconName | SvgIconValue | null {
	switch (activity.kind) {
		case NotificationKind.Announcement:
			return "bullhorn";
		case NotificationKind.KnowledgeItem:
			return "book";
		case NotificationKind.Event:
		case NotificationKind.EventAttendance:
			return "calendar-star";
		case NotificationKind.NewEmail:
			return "envelope";
		case NotificationKind.DocumentShared:
			return {
				kind: "svg",
				name: getDocumentKind(
					activity.data.documentType,
					`.${activity.data.extension}`
				)
			};
		case NotificationKind.Community:
		case NotificationKind.CommunityMembership:
			return "users";
		default:
			return null;
	}
}

function getPersonAvatar(activity: NotificationViewModel): PersonAvatarData | null {
	switch (activity.kind) {
		case NotificationKind.Birthday:
		case NotificationKind.BirthdayWish:
		case NotificationKind.BirthdayWishReaction:
		case NotificationKind.AnnouncementComment:
		case NotificationKind.AnnouncementCommentReaction:
		case NotificationKind.AnnouncementReaction:
		case NotificationKind.AnnouncementCommentMention:
		case NotificationKind.CommunityPostComment:
		case NotificationKind.CommunityPostCommentMention:
		case NotificationKind.EventReaction:
		case NotificationKind.EventComment:
		case NotificationKind.EventCommentMention:
		case NotificationKind.EventCommentReaction:
		case NotificationKind.KnowledgeItemComment:
		case NotificationKind.KnowledgeItemCommentReaction:
		case NotificationKind.KnowledgeItemCommentMention:
		case NotificationKind.KnowledgeItemReaction:
			return {
				initials: getComputedDisplayName({
					firstName: activity.data.firstname,
					lastName: activity.data.lastname
				})
					.initials,
				imageSource: activity.data.avatarId
					? getUserProfileImageUrl(activity.data.avatarId)
					: null
			};
		case NotificationKind.CommunityPostReaction:
		case NotificationKind.CommunityPostCommentReaction:
		case NotificationKind.CommunityPost:
			const { user } = activity.data;

			const { initials } = isNotNil(user)
				? getComputedDisplayName({
					firstName: user.firstName,
					lastName: user.lastName
				})
				: getComputedDisplayName({
					displayName: localize("_DeletedUser")
				});

			return {
				initials,
				imageSource: user?.avatarId
					? getUserProfileImageUrl(user?.avatarId)
					: null
			};
		default:
			return null;
	}
}

function activityKindToAppType(
	activity: InvocableAvailableNotificationViewModel
): AppType | null {
	switch (activity.kind) {
		case NotificationKind.Announcement:
		case NotificationKind.AnnouncementComment:
		case NotificationKind.AnnouncementReaction:
		case NotificationKind.AnnouncementCommentReaction:
		case NotificationKind.AnnouncementCommentMention:
		case NotificationKind.KnowledgeItem:
		case NotificationKind.Event:
		case NotificationKind.EventAttendance:
		case NotificationKind.CommunityPost:
		case NotificationKind.CommunityPostComment:
		case NotificationKind.CommunityPostCommentMention:
		case NotificationKind.Community:
		case NotificationKind.CommunityMembership:
		case NotificationKind.CommunityPostReaction:
		case NotificationKind.CommunityPostCommentReaction:
		case NotificationKind.EventReaction:
		case NotificationKind.EventComment:
		case NotificationKind.EventCommentMention:
		case NotificationKind.EventCommentReaction:
		case NotificationKind.KnowledgeItemComment:
		case NotificationKind.KnowledgeItemCommentReaction:
		case NotificationKind.KnowledgeItemCommentMention:
		case NotificationKind.KnowledgeItemReaction:
			return AppType.Hub;
		case NotificationKind.NewEmail:
			return AppType.Email;
		case NotificationKind.DocumentShared:
			return AppType.Documents;
		case NotificationKind.NewMainAppPromotion:
		case NotificationKind.AppAccess:
		case NotificationKind.Maintenance:
		case NotificationKind.CrossStoreOperation:
		case NotificationKind.Custom:
		case NotificationKind.UserRequest:
			return null;
		default:
			never(activity);
	}
}
